// Safety awareness exporters — the briefing evidence trail as a styled Excel workbook
// and an HPR-branded PDF. This is the artefact a contractor safety review, an insurer
// or a WHS inspector asks for: WHICH topic, WHO read it, WHEN, and in what language.
//
// A "row" is: { topic, login, name, area, lang, first, last, count }
// A hazard row adds: { address, action }
// `rangeLabel` names the period the register covers — a filtered register that doesn't
// say what it was filtered to is the one thing an auditor cannot use.
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// The house palette — the same black band and gold wordmark as the van lease
// statement, the condition report and the accident report, so every document the
// engine hands out looks like it came from the same office.
const GOLD = rgb(0.788, 0.659, 0.298);
const BLACK = rgb(0.07, 0.07, 0.10);
const INK = rgb(0.10, 0.10, 0.18);
const GREY = rgb(0.45, 0.45, 0.50);

const HPR_NAME = 'HOLLYPAUL RECRUITMENT PTY LTD';
const HPR_ABN = '60 651 664 885';
const DOC_TITLE = 'Safety Awareness Register';

// width = Excel char width; pw = PDF point width (relative, scaled to the page).
const COLS = [
  { key: 'topic', label: 'Safety topic', width: 26, pw: 118, align: 'left' },
  { key: 'login', label: 'Login', width: 14, pw: 68, align: 'left' },
  { key: 'name', label: 'Name', width: 24, pw: 108, align: 'left' },
  { key: 'area', label: 'Delivery area', width: 18, pw: 88, align: 'left' },
  { key: 'lang', label: 'Language', width: 12, pw: 54, align: 'left' },
  { key: 'last', label: 'Read (date & time)', width: 21, pw: 96, align: 'left' },
  { key: 'first', label: 'First read', width: 21, pw: 96, align: 'left' },
  { key: 'count', label: 'Times', width: 8, pw: 34, align: 'right' },
];

const HZ_COLS = [
  { key: 'login', label: 'Login', width: 14, pw: 62, align: 'left' },
  { key: 'name', label: 'Name', width: 24, pw: 100, align: 'left' },
  { key: 'area', label: 'Delivery area', width: 18, pw: 78, align: 'left' },
  { key: 'address', label: 'Address', width: 34, pw: 140, align: 'left' },
  { key: 'action', label: 'Instruction', width: 20, pw: 76, align: 'left' },
  { key: 'lang', label: 'Language', width: 12, pw: 48, align: 'left' },
  { key: 'last', label: 'Read (date & time)', width: 21, pw: 92, align: 'left' },
];

const cell = (r, key) => {
  const v = r[key];
  if (v == null || v === '') return '';
  return String(v);
};

// The PDF's standard fonts are WinAnsi: anything outside it — an emoji, a Chinese
// character in a name — throws on draw and would take the whole download with it.
// Emoji get drawn as pictures instead (below); anything else unencodable is dropped
// rather than allowed to kill the file.
const KEEP = /[\u2018\u2019\u201C\u201D\u2013\u2014\u2022\u2026\u20AC]/;
const winAnsi = (str) => String(str == null ? '' : str)
  .split('')
  .filter((ch) => (ch.charCodeAt(0) >= 32 && ch.charCodeAt(0) <= 255) || KEEP.test(ch))
  .join('');

// A topic's emoji, rendered once per distinct character to a PNG and embedded. The
// canvas is the browser's — in a headless test there is none, and the register simply
// prints without the pictures.
async function embedEmoji(pdf, chars) {
  const out = {};
  for (const ch of chars) {
    if (!ch || out[ch]) continue;
    try {
      const canvas = document.createElement('canvas');
      if (!canvas || typeof canvas.getContext !== 'function') return out;
      canvas.width = 72; canvas.height = 72;
      const ctx = canvas.getContext('2d');
      if (!ctx) return out;
      ctx.font = '56px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(ch, 36, 38);
      out[ch] = await pdf.embedPng(canvas.toDataURL('image/png'));
    } catch { /* one bad glyph must not cost the register */ }
  }
  return out;
}

const esc = (t) => String(t == null ? '' : t).replace(/&/g, '&&');
const fileStamp = () => new Date().toISOString().slice(0, 10);
// A downloaded range goes in the filename too, so two exports never collide in a
// downloads folder and the period survives being emailed on.
const stamp = (fileTag) => (fileTag ? String(fileTag).replace(/[^0-9a-zA-Z_-]/g, '') : fileStamp());

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// ── Excel ─────────────────────────────────────────────────────────────────────
export async function downloadAwarenessXlsx({ rows, hazardRows, generatedAt, rangeLabel, scopeLabel, fileTag }) {
  const mod = await import('exceljs/dist/exceljs.min.js');
  const ExcelJS = (mod.default && mod.default.Workbook) ? mod.default : (mod.Workbook ? mod : (mod.default || mod));
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Hollypaul App Engine';

  const sheet = (name, cols, data, subtitle) => {
    const ws = wb.addWorksheet(name, {
      views: [{ state: 'frozen', ySplit: 4 }],
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 } },
      headerFooter: {
        // '&' starts a directive in an Excel header/footer, so anything interpolated
        // in ("Dogs & Animals") has to double it or the rest of the line disappears.
        oddHeader: `&L&"Helvetica,Bold"&14HOLLYPAUL RECRUITMENT&R&"Helvetica"&10${esc([DOC_TITLE, scopeLabel, rangeLabel].filter(Boolean).join(' — '))}`,
        oddFooter: `&L&8${esc(generatedAt)}&C&8Confidential&R&8Page &P of &N`,
      },
    });
    const nCols = cols.length;
    cols.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

    ws.mergeCells(1, 1, 1, nCols);
    const t1 = ws.getCell(1, 1);
    t1.value = DOC_TITLE;
    t1.font = { name: 'Helvetica', size: 16, bold: true, color: { argb: 'FF121218' } };
    ws.getRow(1).height = 22;

    ws.mergeCells(2, 1, 2, nCols);
    const t2 = ws.getCell(2, 1);
    t2.value = [scopeLabel, rangeLabel, subtitle].filter(Boolean).join('    ·    ');
    t2.font = { name: 'Helvetica', size: 10, color: { argb: 'FF6B6B76' } };

    ws.mergeCells(3, 1, 3, nCols);
    const t3 = ws.getCell(3, 1);
    t3.value = `${HPR_NAME} · ABN ${HPR_ABN}    ·    ${generatedAt || ''}`;
    t3.font = { name: 'Helvetica', size: 9, italic: true, color: { argb: 'FF9A9AA2' } };

    const headerRow = ws.getRow(4);
    cols.forEach((c, i) => {
      const h = headerRow.getCell(i + 1);
      h.value = c.label;
      h.font = { name: 'Helvetica', size: 10, bold: true, color: { argb: 'FF121218' } };
      h.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC9A84C' } };
      h.alignment = { vertical: 'middle', horizontal: c.align, wrapText: true };
    });
    headerRow.height = 20;

    data.forEach((r, ri) => {
      const row = ws.getRow(5 + ri);
      cols.forEach((c, i) => {
        const target = row.getCell(i + 1);
        if (c.key === 'count') {
          const n = r.count;
          target.value = (n == null || n === '') ? '' : Number(n);
        } else if (c.key === 'topic' && r.emoji) {
          target.value = `${r.emoji} ${cell(r, c.key)}`;
        } else {
          target.value = cell(r, c.key);
        }
        target.font = { name: 'Helvetica', size: 10, color: { argb: 'FF222228' } };
        target.alignment = { vertical: 'top', horizontal: c.align, wrapText: c.key === 'address' };
        target.border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } } };
      });
      if (ri % 2 === 1) {
        row.eachCell({ includeEmpty: true }, (cc) => {
          cc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9F6EF' } };
        });
      }
    });
    if (!data.length) {
      const row = ws.getRow(5);
      row.getCell(1).value = 'Nothing acknowledged yet.';
      row.getCell(1).font = { name: 'Helvetica', size: 10, italic: true, color: { argb: 'FF9A9AA2' } };
    }
    return ws;
  };

  sheet('Safety Briefings', COLS, rows,
    scopeLabel ? 'Every driver who has acknowledged this brief, with the date and time they read it'
      : 'Every safety topic acknowledged by a driver, with the date and time they read it');
  // A null hazardRows means the caller narrowed to one topic — address hazards are not
  // part of that register, so the sheet is left out rather than shown empty.
  if (Array.isArray(hazardRows)) {
    sheet('Hazard Acknowledgements', HZ_COLS, hazardRows, 'Every address hazard a driver has tapped through, kept after the hazard is cleared');
  }

  const buf = await wb.xlsx.writeBuffer();
  triggerDownload(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `Safety-Awareness-Register_${stamp(fileTag)}.xlsx`);
}

// ── PDF ───────────────────────────────────────────────────────────────────────
export async function downloadAwarenessPdf({ rows, hazardRows, generatedAt, rangeLabel, scopeLabel, fileTag }) {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const PW = 841.89, PH = 595.28;       // A4 landscape — eight columns need the width
  const M = 30, BAND = 72, FOOT = 34;
  const tableW = PW - 2 * M;

  const emoji = await embedEmoji(pdf, [...new Set((rows || []).map(r => r.emoji).filter(Boolean))]);
  const EM = 9.5;   // drawn square, in points — sits on the topic's first line

  let page, y, cols = COLS, colW = [], sectionTitle = '';

  const clip = (text, f, size, maxW) => {
    let s = winAnsi(text);
    if (f.widthOfTextAtSize(s, size) <= maxW) return s;
    while (s.length && f.widthOfTextAtSize(s + '…', size) > maxW) s = s.slice(0, -1);
    return s + '…';
  };

  const wrap = (text, f, size, maxW) => {
    const words = winAnsi(text).split(/\s+/).filter(Boolean);
    const lines = []; let line = '';
    for (const w of words) {
      const trial = line ? line + ' ' + w : w;
      if (f.widthOfTextAtSize(trial, size) > maxW && line) { lines.push(line); line = w; }
      else line = trial;
    }
    if (line) lines.push(line);
    return lines.length ? lines : [''];
  };

  const drawHeaderBand = () => {
    page.drawRectangle({ x: 0, y: PH - BAND, width: PW, height: BAND, color: BLACK });
    page.drawText('HOLLYPAUL', { x: M, y: PH - 40, size: 24, font: bold, color: GOLD });
    page.drawText(HPR_NAME, { x: M, y: PH - BAND + 12, size: 7, font, color: rgb(0.62, 0.62, 0.68) });
    page.drawText(DOC_TITLE, { x: PW - M - bold.widthOfTextAtSize(DOC_TITLE, 15), y: PH - 32, size: 15, font: bold, color: GOLD });
    const sub = sectionTitle;
    page.drawText(sub, { x: PW - M - font.widthOfTextAtSize(sub, 8.5), y: PH - 46, size: 8.5, font, color: rgb(0.85, 0.85, 0.90) });
    const gen = winAnsi([scopeLabel, rangeLabel, generatedAt].filter(Boolean).join('  ·  '));
    page.drawText(gen, { x: PW - M - font.widthOfTextAtSize(gen, 7.5), y: PH - 58, size: 7.5, font, color: rgb(0.66, 0.66, 0.72) });
    page.drawRectangle({ x: 0, y: PH - BAND - 3, width: PW, height: 3, color: GOLD });
  };

  const drawColHeaders = () => {
    y = PH - BAND - 24;
    page.drawRectangle({ x: M, y: y - 5, width: tableW, height: 18, color: GOLD });
    let x = M;
    cols.forEach((c, i) => {
      const lbl = clip(c.label, bold, 8, colW[i] - 6);
      const tx = c.align === 'right' ? x + colW[i] - 4 - bold.widthOfTextAtSize(lbl, 8) : x + 4;
      page.drawText(lbl, { x: tx, y: y + 1, size: 8, font: bold, color: BLACK });
      x += colW[i];
    });
    y -= 12;
  };

  const newPage = () => {
    page = pdf.addPage([PW, PH]);
    drawHeaderBand(); drawColHeaders();
  };

  const size = 8, lineH = 10.5, topPad = 9, botPad = 5;

  const drawSection = (title, sectionCols, data, emptyText) => {
    cols = sectionCols;
    sectionTitle = title;
    const rawW = cols.reduce((a, c) => a + c.pw, 0);
    colW = cols.map((c) => (c.pw / rawW) * tableW);
    newPage();

    if (!data.length) {
      page.drawText(emptyText, { x: M + 4, y: y - 14, size: 9, font, color: GREY });
      return;
    }

    const wrapKeys = ['topic', 'name', 'address', 'area', 'action'];
    data.forEach((r, ri) => {
      const lines = {};
      let maxLines = 1;
      const badge = r.emoji ? emoji[r.emoji] : null;
      const padOf = (c) => (c.key === 'topic' && badge ? 4 + EM + 4 : 4);
      cols.forEach((c, i) => {
        if (wrapKeys.includes(c.key)) {
          lines[c.key] = wrap(cell(r, c.key), c.key === 'name' ? bold : font, size, colW[i] - padOf(c) - 4);
          maxLines = Math.max(maxLines, lines[c.key].length);
        }
      });
      const rowH = (maxLines - 1) * lineH + topPad + botPad;
      if (y - rowH < FOOT) newPage();

      if (ri % 2 === 1) {
        page.drawRectangle({ x: M, y: y - rowH, width: tableW, height: rowH, color: rgb(0.976, 0.965, 0.937) });
      }
      const baseY = (li) => y - topPad - li * lineH;
      let x = M;
      cols.forEach((c, i) => {
        if (lines[c.key]) {
          if (c.key === 'topic' && badge) {
            page.drawImage(badge, { x: x + 4, y: baseY(0) - 1.5, width: EM, height: EM });
          }
          lines[c.key].forEach((ln, li) => page.drawText(ln, {
            x: x + padOf(c), y: baseY(li), size, font: c.key === 'name' ? bold : font, color: INK,
          }));
        } else {
          const val = clip(cell(r, c.key), font, size, colW[i] - 6);
          const tx = c.align === 'right' ? x + colW[i] - 4 - font.widthOfTextAtSize(val, size) : x + 4;
          page.drawText(val, { x: tx, y: baseY(0), size, font, color: INK });
        }
        x += colW[i];
      });
      y -= rowH;
      page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.4, color: rgb(0.90, 0.89, 0.85) });
    });
  };

  const ranged = !!rangeLabel && !/^All acknowledgements/i.test(rangeLabel);
  drawSection(scopeLabel ? `Who has been briefed on ${scopeLabel}, and when` : 'Who has been briefed on what, and when',
    COLS, rows,
    ranged ? 'No safety topic was acknowledged in this period.' : 'Nobody has acknowledged a safety topic yet.');
  // null = one topic was asked for; address hazards are not part of that register.
  if (Array.isArray(hazardRows)) {
    drawSection('Address hazards acknowledged', HZ_COLS, hazardRows,
      ranged ? 'No address hazard was acknowledged in this period.' : 'No address hazards have been acknowledged yet.');
  }

  const pages = pdf.getPages();
  pages.forEach((pg, i) => {
    pg.drawLine({ start: { x: M, y: FOOT - 6 }, end: { x: PW - M, y: FOOT - 6 }, thickness: 0.5, color: rgb(0.8, 0.8, 0.8) });
    pg.drawText(`${HPR_NAME} · ABN ${HPR_ABN} · ${DOC_TITLE}`, { x: M, y: FOOT - 18, size: 7, font, color: GREY });
    pg.drawText('Confidential', { x: PW / 2 - font.widthOfTextAtSize('Confidential', 7) / 2, y: FOOT - 18, size: 7, font, color: GREY });
    const pn = `Page ${i + 1} of ${pages.length}`;
    pg.drawText(pn, { x: PW - M - font.widthOfTextAtSize(pn, 7), y: FOOT - 18, size: 7, font, color: GREY });
  });

  const bytes = await pdf.save();
  triggerDownload(new Blob([bytes], { type: 'application/pdf' }), `Safety-Awareness-Register_${stamp(fileTag)}.pdf`);
}
